import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Sport } from '@sorare/core/src/__generated__/globalTypes';
import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { Actions } from '@sorare/core/src/atoms/layout/Actions';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BodyL, BrandXXL } from '@sorare/core/src/atoms/typography';
import { TextureBackground } from '@sorare/core/src/atoms/ui/TextureBackground';
import { Dialog } from '@sorare/core/src/components/dialog';
import { CloseButton } from '@sorare/core/src/components/dialog/CloseButton';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useCheckForMobilePushNotifications } from '@sorare/core/src/hooks/useCheckForMobilePushNotifications';

import type { SetOnboardingNotificationsPrompt_cardSet } from './__generated__/index.graphql';
import playerNba from './assets/player-nba.webp';
import playerFootball from './assets/player.webp';

const Wrapper = styled.div`
  position: relative;
  height: var(--100vh);
  overflow: hidden;
  isolation: isolate;
`;

const StyledTextureBackground = styled(TextureBackground)`
  position: absolute;
  inset: 0;
  z-index: -2;
`;

const PlayerImage = styled.img`
  position: absolute;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  max-height: 70%;
  width: auto;
  z-index: -1;
  pointer-events: none;
`;

const StyledCloseButton = styled(CloseButton)`
  position: absolute;
  top: var(--unit);
  right: var(--unit);
`;

const TextBlock = styled(Vertical).attrs({ gap: 2 })`
  position: absolute;
  top: var(--quadruple-unit);
  left: var(--double-unit);
  right: var(--double-unit);
  color: var(--c-white);
`;

const Buttons = styled(Actions).attrs({ direction: 'horizontal', gap: 1.5 })`
  position: absolute;
  bottom: var(--triple-unit);
  left: var(--double-unit);
  right: var(--double-unit);
`;

type Props = {
  open: boolean;
  onClose: () => void;
  cardSet?: Nullable<SetOnboardingNotificationsPrompt_cardSet>;
};

export const SetOnboardingNotificationsPrompt = ({
  open,
  onClose,
  cardSet,
}: Props) => {
  const checkForMobilePushNotifications = useCheckForMobilePushNotifications();
  const { sport } = useSportContext();
  const fallbackPlayer = sport === Sport.NBA ? playerNba : playerFootball;
  const player = cardSet?.branding?.dontMissOutPlayer ?? fallbackPlayer;
  return (
    <Dialog open={open} maxWidth="xs" hideHeader>
      <Wrapper>
        <StyledTextureBackground
          background="wave1"
          color="#9E49EA"
          imageOpacity={0.1}
        />
        <PlayerImage src={player} alt="" />
        <StyledCloseButton onClose={onClose} transparent />
        <TextBlock>
          <BrandXXL as="p">
            <FormattedMessage defaultMessage="Don't miss out" />
          </BrandXXL>
          <BodyL>
            <FormattedMessage defaultMessage="Get notified when your game starts to follow your score." />
          </BodyL>
        </TextBlock>
        <Buttons>
          <Button color="quaternary" size="large" onClick={onClose}>
            <FormattedMessage defaultMessage="Maybe later" />
          </Button>
          <Button
            color="secondary"
            size="large"
            onClick={() => {
              checkForMobilePushNotifications();
              onClose();
            }}
          >
            <FormattedMessage defaultMessage="Notify me" />
          </Button>
        </Buttons>
      </Wrapper>
    </Dialog>
  );
};

SetOnboardingNotificationsPrompt.fragments = {
  cardSet: gql`
    fragment SetOnboardingNotificationsPrompt_cardSet on CardSet {
      slug
      branding {
        id
        dontMissOutPlayer: assetUrl(slot: ONBOARDING_DONT_MISS_OUT_PLAYER)
      }
    }
  ` as TypedDocumentNode<SetOnboardingNotificationsPrompt_cardSet>,
};
